<template>
  <div class="c-widget-display-form">
    <section>
      <h2>Triggers</h2>
      <div class="my-4 p-2 space-x-4 flex bg-white border">
        <div class="w-16 pt-4 pr-2 relative flex justify-center border-r-2">
          <el-switch
              v-model="autoTrigger"
              active-color="#C8E85E"
              inactive-color="#000000"></el-switch>
        </div>
        <div class="flex-1">
          <div class="px-4 pb-2">
            <h4 class="font-semibold">Automatic Trigger</h4>
            <p class="text-sm">Let our algorithm find the most effective trigger for your website.</p>
          </div>
        </div>
      </div>

      <div class="my-4 p-2 space-x-4 flex bg-white border">
        <div class="w-16 pt-4 pr-2 relative flex justify-center border-r-2">
          <el-switch
              v-model="ribbonTrigger"
              active-color="#C8E85E"
              inactive-color="#000000"></el-switch>
        </div>
        <div class="flex-1">
          <div :class="['px-4 pb-2', { 'border-b-2': ribbonTrigger }]">
            <h4 class="font-semibold">Ribbon Trigger</h4>
            <p class="text-sm">Let the user engage with the widget.</p>
          </div>

          <div v-show="ribbonTrigger" class="p-4 grid grid-cols-2 gap-x-20 gap-y-4">

            <div class="flex text-sm justify-between">
              <span class="mr-8 text-sm font-medium">Type</span>


              <el-radio-group v-model="ribbonType">
                <el-radio-button :label="ribbonTypeOptions.TEXT">
                  <div class="flex">
                    <p class="mr-2">Text</p>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon w-4 focus:outline-none'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><path d="M6 8h12M6 12h12M6 16h12"/></svg>
                  </div>
                </el-radio-button>

                <el-radio-button :label="ribbonTypeOptions.GIFT">
                  <div class="flex">
                    <p class="mr-2">Gift</p>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 330 330" v-bind:svg-inline="''" v-bind:class="'icon w-4 focus:outline-none'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M295.233 70.253a49.863 49.863 0 004.27-20.247c0-13.361-5.204-25.92-14.65-35.36C275.407 5.201 262.85 0 249.494 0c-13.357 0-25.914 5.202-35.356 14.645l-49.136 49.13-49.13-49.131C106.424 5.201 93.866 0 80.512 0S54.6 5.201 45.154 14.643C35.704 24.087 30.5 36.646 30.5 50.006c0 7.092 1.48 13.953 4.269 20.247-6.961 1.299-12.235 7.396-12.235 14.734v70c0 8.284 6.716 15 15 15h1.328V315c0 8.284 6.716 15 15 15H276.14c8.284 0 15-6.716 15-15V169.988h1.326c8.284 0 15-6.716 15-15v-70c0-7.338-5.272-13.435-12.233-14.735zm-59.882-34.395c3.777-3.778 8.8-5.858 14.143-5.858s10.367 2.081 14.148 5.862c3.779 3.777 5.86 8.8 5.86 14.144s-2.081 10.367-5.863 14.147c-3.75 3.75-8.745 5.818-14.068 5.834h-48.353l34.133-34.129zm-70.35 70.343l33.789 33.787h-67.578l33.789-33.787zm-98.639-70.34C70.143 32.082 75.168 30 80.512 30s10.369 2.082 14.148 5.859l34.128 34.129H80.432c-5.324-.016-10.318-2.084-14.071-5.837a19.865 19.865 0 01-5.86-14.144 19.858 19.858 0 015.861-14.146zM52.535 139.988v-40h27.837c.041 0 .08.003.121.003l48.293-.001-40.001 39.998h-36.25zM150.002 300H68.863V169.988h81.139V300zm111.138 0h-81.139V169.988h81.139V300zm16.326-160.012h-36.248l-40.002-40h47.824l.47.003c.041 0 .08-.003.121-.003h27.835v40z"/></svg>
                  </div>
                </el-radio-button>

              </el-radio-group>
            </div>

            <div class="flex gap-8 justify-between">
              <span class="text-sm font-medium">Background Color</span>
              <edit-block-color-picker
                  v-model="ribbonBgColor"
                  :inline-title="true"/>
            </div>

            <div class="flex gap-8 justify-between">
              <span class="text-sm font-medium">Position</span>
              <div>
                <el-radio-group v-model="ribbonPosition">
                  <el-radio-button label="bottomLeft">
                    <p>Bottom Left</p>
                  </el-radio-button>
                  <el-radio-button label="bottomRight">
                    <p>Bottom Right</p>
                  </el-radio-button>
                </el-radio-group>
              </div>
            </div>

            <div class="flex gap-8 justify-between">
              <span class="text-sm font-medium">Color</span>
              <edit-block-color-picker
                  v-model="ribbonColor"
                  :inline-title="true"/>
            </div>

            <div v-if="ribbonType === ribbonTypeOptions.TEXT" class="flex gap-8 justify-between">
              <span class="text-sm font-medium">Text</span>
              <div>
                <edit-block-text-input
                    v-model="ribbonText"
                    :placeholder="'Text'"
                    :maxlength="24"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="my-4 p-2 space-x-4 flex bg-white border">
        <div class="w-16 pt-4 pr-2 relative flex justify-center border-r-2">
          <el-switch
              v-model="timeTrigger"
              active-color="#C8E85E"
              inactive-color="#000000"></el-switch>
        </div>
        <div class="flex-1">
          <div :class="['px-4 pb-2', { 'border-b-2': timeTrigger }]">
            <h4 class="font-semibold">Show after</h4>
            <p class="text-sm">Show when a visitor has been on a
              page for more than X seconds.</p>
          </div>

          <div v-show="timeTrigger" class="p-4">
            <div class="flex items-center text-sm">
              <span>Show after</span>
              <el-input-number v-model="triggerSeconds" class="mx-2 gray"
                               controls-position="right" :min="1" :max="10"></el-input-number>
              <span>seconds on your page</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <h2>Frequency</h2>
      <div class="my-4 p-2 space-x-4 flex bg-white border">
        <div class="w-16 pt-4 pr-2 relative flex justify-center border-r-2">
          <i class="el-icon-time text-4xl"></i>
        </div>
        <div class="flex-1">
          <div :class="['px-4 pb-2', { 'border-b-2': ribbonTrigger }]">
            <h4 class="font-semibold">Timing</h4>
            <p class="text-sm">Set how often the message should reappear to users.</p>
          </div>

          <div class="p-4">
            <div>
              <div>
                <span class="text-sm font-medium">The popup will appear a maximum number of</span>

                <edit-block-numeric-input
                    v-model="displayCount"
                    class="mx-2 inline-block"
                    :input-class-list="'w-12'"
                    :min="0"
                    :max="100"/>
                <span class="text-sm font-medium">times per visitor,</span>
              </div>
              <div class="mt-2">
               <span class="text-sm font-medium"> with at least</span>
                <edit-block-numeric-input
                    v-model="displayFrequency"
                    class="mx-2 inline-block"
                    :min="0"
                    :max="100"/>

                <el-select
                    v-model="displayFrequencyPeriod"
                    class="mr-2"
                    size="small">
                  <el-option
                      v-for="item in [
                        { label: 'second(s)', value: frequencyPeriodOptions.SECOND },
                        { label: 'minute(s)', value: frequencyPeriodOptions.MINUTE },
                        { label: 'hour(s)', value: frequencyPeriodOptions.HOUR },
                        { label: 'day(s)', value: frequencyPeriodOptions.DAY },
                      ]"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value">
                  </el-option>
                </el-select>
                <span class="text-sm font-medium">between two appearances.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <h2>Widget Display</h2>
      <div class="my-4 p-2 space-x-4 flex bg-white border">
        <div class="w-16 pt-4 pr-2 relative flex justify-center border-r-2">
          <i class="el-icon-monitor text-3xl"></i>
        </div>

        <div class="mx-2.5 flex flex-col">
          <div class="mb-1 text-sm">
            <span class="font-medium">Position</span>
          </div>

          <div class="flex items-end justify-between">
            <edit-block-position
                v-model="position"/>
            <!--            <div class="ml-4 flex flex-col">-->
            <!--              <button class="btn secondary" type="button">Preview</button>-->
            <!--            </div>-->
          </div>
        </div>
        <div class="mx-2.5 flex flex-col">
          <span class="mb-1 text-sm font-medium">Animation</span>
          <el-select class="gray" v-model="animation" placeholder="Select">
            <el-option
                v-for="item in [
                  // { label: 'None', value: '' },
                  { label: 'Fade', value: animationOptions.FADE },
                  { label: 'Slide-in', value: animationOptions.SLIDE },
                  { label: 'Zoom', value: animationOptions.ZOOM },
                ]"
                :key="item.value"
                :label="item.label"
                :value="item.value">
            </el-option>
          </el-select>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  EditBlockPosition,
  EditBlockColorPicker,
  EditBlockTextInput,
  EditBlockNumericInput,
} from '@components/EditBuildingBlocks/all'
import { frequencyPeriod, ribbonType, trigger, animation } from '@meta/widget/options'
import { mapActions } from 'vuex'
import _debounce from 'lodash/debounce'

export default {
  name: 'DisplaySettingsSection',
  components: {
    'edit-block-position': EditBlockPosition,
    'edit-block-text-input': EditBlockTextInput,
    'edit-block-numeric-input': EditBlockNumericInput,
    'edit-block-color-picker': EditBlockColorPicker,
  },
  props: {
    widget: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      autoTrigger: false,
      ribbonTrigger: false,
      timeTrigger: false,
      enableActions: false,
      frequencyPeriodOptions: frequencyPeriod,
      ribbonTypeOptions: ribbonType,
      triggerOptions: trigger,
      animationOptions: animation,
    }
  },
  computed: {
    trigger: {
      get() {
        return this.widget.settings.trigger
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['trigger'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    triggerSeconds: {
      get() {
        return this.widget.settings.triggerSeconds
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['triggerSeconds'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    ribbonType: {
      get() {
        return this.widget.settings.ribbonType
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['ribbonType'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    ribbonPosition: {
      get() {
        return this.widget.settings.ribbonPosition
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['ribbonPosition'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    ribbonText: {
      get() {
        return this.widget.settings.ribbonText
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['ribbonText'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    ribbonColor: {
      get() {
        return this.widget.settings.ribbonColor
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['ribbonColor'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    ribbonBgColor: {
      get() {
        return this.widget.settings.ribbonBgColor
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['ribbonBgColor'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    displayCount: {
      get() {
        return this.widget.settings.displayCount
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['displayCount'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    displayFrequency: {
      get() {
        return this.widget.settings.displayFrequency
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['displayFrequency'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    displayFrequencyPeriod: {
      get() {
        return this.widget.settings.displayFrequencyPeriod
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['displayFrequencyPeriod'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    animation: {
      get() {
        return this.widget.settings.animation
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['animation'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
    position: {
      get() {
        return this.widget.settings.position
      },
      set(value) {
        if (this.enableActions) {
          this.updateAllWidgetsSettings({ path: ['position'], settings: value })
          this.debouncedSaveGlobal()
        }
      },
    },
  },
  watch: {
    autoTrigger(active) {
      if (active && this.enableActions) {
        this.timeTrigger = false
        this.ribbonTrigger = false
        this.trigger = this.triggerOptions.AUTO
      }
    },
    ribbonTrigger(active) {
      if (active && this.enableActions) {
        this.autoTrigger = false
        this.timeTrigger = false
        this.trigger = this.triggerOptions.RIBBON
      }
    },
    timeTrigger(active) {
      if (active && this.enableActions) {
        this.autoTrigger = false
        this.ribbonTrigger = false
        this.trigger = this.triggerOptions.TIME
      } else {
        this.trigger = this.triggerOptions.AUTO
      }
    },
  },
  created() {
    // Set the initial values for autoTrigger and timeTrigger based on this.trigger
    if (this.widget.settings.trigger === this.triggerOptions.AUTO) {
      this.autoTrigger = true
    } else if (this.widget.settings.trigger === this.triggerOptions.TIME) {
      this.timeTrigger = true
    } else if (this.widget.settings.trigger === this.triggerOptions.RIBBON) {
      this.ribbonTrigger = true
    }

    this.$nextTick(() => {
      this.enableActions = true
    })
  },
  methods: {
    ...mapActions('campaign/widget', ['updateAllWidgetsSettings']),
    ...mapActions('editor', ['saveGlobal']),
    debouncedSaveGlobal: _debounce(function () {
      this.saveGlobal()
    }, 450),
  },
}
</script>

<style lang="scss" module>
.radioGroup {
  label {
    @apply flex items-center justify-center;
    aspect-ratio: 1/1;
  }

  span {
    @apply h-full flex border-0 rounded-none outline-none #{!important};
  }

  svg {
    @apply outline-none;
  }
}
</style>